import { buildFireworkSound } from "./fireworkSound";
import { FIREWORK_VOICE_LIMIT, getFireworkVoiceGain } from "./fireworkMix";
import type { FireworkCue } from "../rendering/celebration/fireworkScore";

class FireworkAudio {
  private context: AudioContext | null = null;
  private master: GainNode | null = null;
  private buffers: AudioBuffer[] = [];
  private voices = new Set<AudioBufferSourceNode>();
  private enabled = false;

  async unlock() {
    try {
      if (!this.context) {
        const AudioContextClass = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
        if (!AudioContextClass) return;
        const context = new AudioContextClass();
        this.context = context;
        this.master = context.createGain();
        this.master.gain.value = this.enabled ? 0.52 : 0;
        const compressor = context.createDynamicsCompressor();
        compressor.threshold.value = -14;
        compressor.knee.value = 10;
        compressor.ratio.value = 5;
        this.master.connect(compressor).connect(context.destination);
        this.buffers = [193, 571, 829].map(seed => {
          const buffer = context.createBuffer(1, Math.round(context.sampleRate * 2.8), context.sampleRate);
          buffer.getChannelData(0).set(buildFireworkSound(context.sampleRate, seed));
          return buffer;
        });
      }
      await this.context.resume();
    } catch { /* Sound is optional; a blocked audio context never blocks the show. */ }
  }

  setEnabled(enabled: boolean) {
    this.enabled = enabled;
    if (this.context && this.master) this.master.gain.setTargetAtTime(enabled ? 0.52 : 0, this.context.currentTime, 0.035);
    if (!enabled) this.stop();
  }

  playBurst(cue: FireworkCue) {
    const context = this.context;
    if (!this.enabled || !context || context.state !== "running" || document.hidden || !this.master) return;
    if (this.voices.size >= FIREWORK_VOICE_LIMIT) return;
    const source = context.createBufferSource();
    source.buffer = this.buffers[cue.palette % this.buffers.length];
    source.playbackRate.value = 0.9 + (1.2 - cue.power) * 0.25;
    const gain = context.createGain();
    gain.gain.value = getFireworkVoiceGain(cue, this.voices.size);
    const pan = context.createStereoPanner();
    pan.pan.value = Math.max(-0.7, Math.min(0.7, cue.x * 0.36));
    const filter = context.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.value = 5600 - Math.abs(cue.z) * 1800;
    source.connect(filter).connect(gain).connect(pan).connect(this.master);
    this.voices.add(source);
    source.onended = () => { this.voices.delete(source); source.disconnect(); filter.disconnect(); gain.disconnect(); pan.disconnect(); };
    source.start();
  }

  stop() {
    this.voices.forEach(source => { try { source.stop(); } catch { /* Already stopped. */ } });
    this.voices.clear();
  }

  async suspend() { this.stop(); try { await this.context?.suspend(); } catch { /* Optional audio. */ } }

  dispose() {
    this.stop();
    void this.context?.close().catch(() => undefined);
    this.context = null;
    this.master = null;
    this.buffers = [];
    this.enabled = false;
  }
}

export const fireworkAudio = new FireworkAudio();
